// Adapted from our own Immersive Worlds WorldStore.
// Source blob: 54152e89452c78db6f24f0057d68974340738ff3

import { RECORD, SCHEMA_VERSION, SPACE_KIND, ENTITY_KIND, FICTION_STATUS, HOTSPOT_TYPE, ACTION } from './types.js';

const VALUES = (obj) => new Set(Object.values(obj));
const SPACE_KINDS = VALUES(SPACE_KIND);
const ENTITY_KINDS = VALUES(ENTITY_KIND);
const FICTION_STATES = VALUES(FICTION_STATUS);
const HOTSPOT_TYPES = VALUES(HOTSPOT_TYPE);
const ACTIONS = VALUES(ACTION);

export class WorldStore {
  constructor(world) {
    const report = validateWorld(world);
    if (!report.ok) {
      const error = new Error(`[WCB] World validation failed:\n- ${report.errors.join('\n- ')}`);
      error.report = report;
      throw error;
    }

    this.id = world.id;
    this.title = world.title;
    this.schemaVersion = world.schemaVersion;
    this.sceneKit = world.sceneKit;
    this.startSpaceId = world.startSpaceId;
    this.metadata = world.metadata || {};
    this._byId = new Map();
    this._byKind = new Map(Object.values(RECORD).map((kind) => [kind, new Map()]));

    this._ingest(RECORD.SPACE, world.spaces);
    this._ingest(RECORD.ANCHOR, world.anchors);
    this._ingest(RECORD.ENTITY, world.entities);
    this._ingest(RECORD.HOTSPOT, world.hotspots);
    this._ingest(RECORD.PORTAL, world.portals);
    this._ingest(RECORD.ROUTE, world.routes);
  }

  _ingest(kind, records = []) {
    const index = this._byKind.get(kind);
    for (const record of records) {
      if (this._byId.has(record.id)) throw new Error(`[WCB] duplicate canonical id: ${record.id}`);
      this._byId.set(record.id, { kind, record });
      index.set(record.id, record);
    }
  }

  get(id) { return this._byId.get(id)?.record; }
  has(id) { return this._byId.has(id); }
  kindOf(id) { return this._byId.get(id)?.kind; }

  require(id, expectedKind) {
    const hit = this._byId.get(id);
    if (!hit) throw new Error(`[WCB] missing canonical record: ${id}`);
    if (expectedKind && hit.kind !== expectedKind) {
      throw new Error(`[WCB] ${id} is ${hit.kind}, expected ${expectedKind}`);
    }
    return hit.record;
  }

  all(kind) { return [...this._byKind.get(kind).values()]; }
  get spaces() { return this.all(RECORD.SPACE); }
  get entities() { return this.all(RECORD.ENTITY); }
  get hotspots() { return this.all(RECORD.HOTSPOT); }
  get portals() { return this.all(RECORD.PORTAL); }
  get routes() { return this.all(RECORD.ROUTE); }

  entitiesOf(spaceId) {
    return (this.require(spaceId, RECORD.SPACE).entityRefs || []).map((id) => this.require(id, RECORD.ENTITY));
  }

  properties() {
    return this.entities.filter((entity) => entity.kind === ENTITY_KIND.PROPERTY);
  }

  propertiesOf(spaceId) {
    return this.entitiesOf(spaceId).filter((entity) => entity.kind === ENTITY_KIND.PROPERTY);
  }

  auditCanonicalIdentity() {
    const violations = [];
    const seenObjects = new Map();
    for (const [id, { kind, record }] of this._byId) {
      if (seenObjects.has(record)) violations.push(`object shared by ${seenObjects.get(record)} and ${id}`);
      seenObjects.set(record, id);
      if (this._byKind.get(kind).get(id) !== record) violations.push(`kind index mismatch: ${id}`);
    }
    return { ok: violations.length === 0, violations, total: this._byId.size };
  }
}

export function validateWorld(world) {
  const errors = [];
  const arrays = ['spaces', 'anchors', 'entities', 'hotspots', 'portals', 'routes'];

  if (!world || typeof world !== 'object') return { ok: false, errors: ['world must be an object'] };
  if (!world.id) errors.push('world.id is required');
  if (world.schemaVersion !== SCHEMA_VERSION) errors.push(`schemaVersion must be ${SCHEMA_VERSION}`);
  if (!world.sceneKit) errors.push('sceneKit is required');
  for (const key of arrays) if (!Array.isArray(world[key])) errors.push(`${key} must be an array`);
  if (errors.length) return { ok: false, errors };

  const all = [];
  for (const key of arrays) for (const record of world[key]) all.push([key, record]);
  const ids = new Set();
  for (const [collection, record] of all) {
    if (!record?.id) errors.push(`${collection} contains record without id`);
    else if (ids.has(record.id)) errors.push(`duplicate id: ${record.id}`);
    else ids.add(record.id);
  }

  if (!ids.has(world.startSpaceId)) errors.push(`startSpaceId not found: ${world.startSpaceId}`);

  for (const space of world.spaces) {
    if (!SPACE_KINDS.has(space.type)) errors.push(`invalid space type ${space.type} on ${space.id}`);
    for (const ref of [...(space.entityRefs || []), ...(space.anchorRefs || []), ...(space.hotspotRefs || []), ...(space.portalRefs || [])]) {
      if (!ids.has(ref)) errors.push(`${space.id} references missing ${ref}`);
    }
    if (space.defaultSpawnAnchorId && !ids.has(space.defaultSpawnAnchorId)) errors.push(`${space.id} missing spawn ${space.defaultSpawnAnchorId}`);
  }

  for (const entity of world.entities) {
    if (!ENTITY_KINDS.has(entity.kind)) errors.push(`invalid entity kind ${entity.kind} on ${entity.id}`);
    if (!FICTION_STATES.has(entity.fictionStatus)) errors.push(`invalid/missing fictionStatus on ${entity.id}`);
    if (!ids.has(entity.spaceId)) errors.push(`${entity.id} references missing space ${entity.spaceId}`);
    if (entity.anchorId && !ids.has(entity.anchorId)) errors.push(`${entity.id} references missing anchor ${entity.anchorId}`);
    if (entity.kind === ENTITY_KIND.PROPERTY && !entity.property) errors.push(`${entity.id} PROPERTY requires property block`);
  }

  for (const hotspot of world.hotspots) {
    if (!HOTSPOT_TYPES.has(hotspot.type)) errors.push(`invalid hotspot type ${hotspot.type} on ${hotspot.id}`);
    if (!ACTIONS.has(hotspot.action?.type)) errors.push(`invalid hotspot action on ${hotspot.id}`);
    if (hotspot.entityId && !ids.has(hotspot.entityId)) errors.push(`${hotspot.id} missing entity ${hotspot.entityId}`);
    if (hotspot.anchorId && !ids.has(hotspot.anchorId)) errors.push(`${hotspot.id} missing anchor ${hotspot.anchorId}`);
  }

  for (const portal of world.portals) {
    for (const ref of [portal.fromSpaceId, portal.toSpaceId, portal.sourceAnchorId, portal.destinationSpawnId]) {
      if (!ids.has(ref)) errors.push(`${portal.id} references missing ${ref}`);
    }
  }

  return { ok: errors.length === 0, errors };
}
